import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import classNames from 'classnames';

import { generateId } from './../../ricos-content';
import { Node_Type, TextStyle_TextAlignment } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { ExtensionProps, NodeConfig, RicosExtension } from './../../ricos-types';
import { createRicosNodeExtension, withBreakoutContainer } from './../../plugin-commons';

import { createSpanWrapperDecorationPlugin } from '../decoration-plugins/span-wrapper';
import styles from '../statics/styles-scss';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    blockQuote: {
      /**
       * Set a blockquote node
       */
      setBlockquote: () => ReturnType;
      /**
       * Toggle a blockquote node
       */
      toggleBlockquote: () => ReturnType;
      /**
       * Unset a blockquote node
       */
      unsetBlockquote: () => ReturnType;
    };
  }
}

const inputRegex = /^\s*>\s$/;
const alignmentToFlex = {
  [TextStyle_TextAlignment.LEFT]: 'left',
  [TextStyle_TextAlignment.CENTER]: 'center',
  [TextStyle_TextAlignment.RIGHT]: 'right',
  [TextStyle_TextAlignment.JUSTIFY]: 'flex-start',
};

export const blockquote = createRicosNodeExtension({
  groups: ['shortcuts-enabled'],
  type: 'html-node',
  name: Node_Type.BLOCKQUOTE,
  reconfigure: (
    config: NodeConfig,
    _extensions: RicosExtension[],
    _props: ExtensionProps,
    settings: Record<string, unknown>
  ) => ({
    ...config,
    addOptions() {
      return {
        HTMLAttributes: {
          class: styles.quote,
        },
        ...settings,
      };
    },
  }),

  createExtensionConfig({ mergeAttributes, textblockTypeInputRule }) {
    return {
      name: this.name,

      // ITS NOT BlockquoteNodeChildrenPattern !!!. ProseMirror and RicosDocument have different structure for blockquote.
      content: 'inline*',

      group:
        'block layoutCellContent collapsibleListItemTitleContent collapsibleListItemBodyContent tableCellContent',

      defining: true,

      addAttributes() {
        return {
          indentation: null,
          textStyle: null,
          paragraphId: null,
        };
      },

      parseHTML() {
        return [{ tag: 'blockquote' }];
      },

      renderHTML({ HTMLAttributes }) {
        const allAttributes = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes);
        const textAlignment: TextStyle_TextAlignment = allAttributes?.textStyle?.textAlignment;

        const attrs = {
          ...allAttributes,
          class: classNames(allAttributes.class),
        };

        const breakoutContainerAttr =
          textAlignment === TextStyle_TextAlignment.AUTO ||
          textAlignment === TextStyle_TextAlignment.UNRECOGNIZED
            ? {
                style: 'display: flex;',
              }
            : {
                style: `display: flex; justify-content: ${alignmentToFlex[textAlignment]};`,
              };
        return withBreakoutContainer(['blockquote', attrs, 0], breakoutContainerAttr);
      },

      addCommands() {
        return {
          setBlockquote:
            () =>
            ({ commands }) => {
              return commands.updateTextNode(this.name, { paragraphId: generateId() });
            },
          toggleBlockquote:
            () =>
            ({ commands }) => {
              //TODO: fix generating paragraphId every render
              return commands.toggleTextNode(this.name, Node_Type.PARAGRAPH);
            },
          unsetBlockquote:
            () =>
            ({ commands }) => {
              return commands.updateTextNode(Node_Type.PARAGRAPH, {});
            },
        };
      },

      addInputRules() {
        return [
          textblockTypeInputRule({
            find: inputRegex,
            type: this.type,
            getAttributes: ({ groups }) => groups,
          }),
        ];
      },

      addProseMirrorPlugins() {
        return [
          createSpanWrapperDecorationPlugin(Node_Type.BLOCKQUOTE),
          new Plugin({
            key: new PluginKey(`blockquote-direction`),
            props: {
              decorations(state) {
                const decorations: Decoration[] = [];

                state.doc.descendants((node, pos) => {
                  if (node.type.name === Node_Type.BLOCKQUOTE) {
                    decorations.push(Decoration.node(pos, pos + node.nodeSize, { dir: 'auto' }));
                  }
                });

                return DecorationSet.create(state.doc, decorations);
              },
            },
          }),
        ];
      },
    };
  },
});
